import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Button,
  TextField,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Card,
  CardContent,
  Stack,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Divider,
  Tooltip,
} from '@mui/material';
import {
  Search,
  Download,
  AccountBalance,
  TrendingUp,
  Assessment,
  Visibility,
  CheckCircle,
  Schedule,
  AttachMoney,
  Add,
  CalendarToday,
  Close,
  Edit,
  Percent,
} from '@mui/icons-material';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, ResponsiveContainer } from 'recharts';
import { supabase } from '../../supabaseClient';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../components/AuthProvider';
import * as XLSX from 'xlsx';

interface WeeklySettlement {
  id: string;
  seller_id: string;
  settlement_type: 'sales' | 'affiliate';
  week_number: number;
  year: number;
  period_start_date: string;
  period_end_date: string;
  gross_sales: number;
  refund_amount: number;
  net_sales: number;
  commission_rate: number;
  commission_amount: number;
  payment_fee_amount: number; // 추가: 결제 수수료 금액
  final_amount: number;
  status: 'pending' | 'processing' | 'completed' | 'cancelled';
  created_at: string;
  due_date: string;
  processed_at?: string;
  // 조인된 데이터
  seller?: {
    id: string;
    user_id: string;
    store_name: string;
    business_number: string;
    business_type: string;
    owner_name: string;
    address: string;
    contact_email: string;
    contact_phone: string;
    bank_name: string;
    bank_account: string;
    bank_account_holder: string;
    status: string;
    payment_fee_rate: number; // 추가: 결제 수수료율
  };
  store_name?: string; // 직접 접근용
}

interface SettlementDetail {
  product_name: string;
  order_count: number;
  total_amount: number;
  commission: number;
  net_amount: number;
  is_affiliate?: boolean;
  affiliate_commission_rate?: number | null;
}


const salesTrendData = [
  { month: '10월', sales: 12000000 },
  { month: '11월', sales: 14500000 },
  { month: '12월', sales: 18200000 },
  { month: '1월', sales: 13800000 },
  { month: '2월', sales: 15600000 },
];

export default function BrandSettlement() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState<'all' | 'sales' | 'affiliate'>('all');
  const [yearFilter, setYearFilter] = useState('all');
  const [weekFilter, setWeekFilter] = useState('all');
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [generateDialogOpen, setGenerateDialogOpen] = useState(false);
  const [selectedSettlement, setSelectedSettlement] = useState<WeeklySettlement | null>(null);
  const [settlements, setSettlements] = useState<WeeklySettlement[]>([]);
  const [settlementDetails, setSettlementDetails] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [weekStart, setWeekStart] = useState('');
  const [weekEnd, setWeekEnd] = useState('');
  const [commissionDialogOpen, setCommissionDialogOpen] = useState(false);
  const [selectedSeller, setSelectedSeller] = useState<{ id: string; store_name: string; commission_rate: number } | null>(null);
  const [newCommissionRate, setNewCommissionRate] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // 사용 가능한 년도/주차 목록
  const availableYears = Array.from(new Set(settlements.map(s => s.year))).sort((a, b) => b - a);
  const availableWeeks = yearFilter === 'all'
    ? []
    : Array.from(new Set(settlements.filter(s => s.year === parseInt(yearFilter)).map(s => s.week_number))).sort((a, b) => b - a);

  // 데이터 로드
  useEffect(() => {
    loadSettlements();
  }, [user]);

  const loadSettlements = async () => {
    if (!user) {
      console.error('User not authenticated');
      return;
    }

    try {
      setLoading(true);

      const { data: profile } = await supabase
        .from('profiles')
        .select('role')
        .eq('id', user.id)
        .single();

      if (profile?.role !== 'admin') {
        console.error('User is not admin');
        return;
      }

      // 주간 정산 데이터 조회 (판매 + 어필리에이트)
      const { data: settlementsData, error: settlementsError } = await supabase
        .from('weekly_settlements')
        .select(`
          *,
          seller:seller_id (
            id,
            user_id,
            store_name,
            business_number,
            business_type,
            owner_name,
            address,
            contact_email,
            contact_phone,
            bank_name,
            bank_account,
            bank_account_holder,
            status
          )
        `)
        .order('year', { ascending: false })
        .order('week_number', { ascending: false });

      if (settlementsError) {
        console.error('Error loading settlements:', settlementsError);
        return;
      }

      // 셀러명 추가
      const formattedSettlements: WeeklySettlement[] = (settlementsData || []).map(s => ({
        ...s,
        store_name: s.seller?.store_name || t('brandSettlement.unknownStore')
      }));

      setSettlements(formattedSettlements);
    } catch (error) {
      console.error('Error loading settlements:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed': return 'success';
      case 'processing': return 'warning';
      case 'pending': return 'info';
      case 'cancelled': return 'error';
      default: return 'default';
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'completed': return t('brandSettlement.completedStatus');
      case 'processing': return t('brandSettlement.processingStatus');
      case 'pending': return t('brandSettlement.pendingStatus');
      case 'cancelled': return t('brandSettlement.cancelledStatus');
      default: return status;
    }
  };

  const handleViewDetail = async (settlement: WeeklySettlement) => {
    setSelectedSettlement(settlement);
    setSettlementDetails([]);

    try {
      if (settlement.settlement_type === 'sales') {
        // 판매 정산 상세 조회 (어필리에이트 수수료율 포함)
        const { data: salesItems, error } = await supabase
          .from('weekly_settlement_sales_items')
          .select(`
            *,
            affiliate_commission_rate:order_item_id (
              commission_rate:affiliate_earnings!order_item_id(commission_rate)
            )
          `)
          .eq('settlement_id', settlement.id)
          .order('order_date', { ascending: false });

        if (error) {
          console.error('Error loading sales items:', error);
          return;
        }

        // 어필리에이트 수수료율 추가
        const formattedSalesItems = (salesItems || []).map(item => ({
          ...item,
          affiliate_commission_rate: item.affiliate_commission_rate?.commission_rate?.[0]?.commission_rate || null
        }));

        setSettlementDetails(formattedSalesItems);
      } else {
        // 어필리에이트 정산 상세 조회
        const { data: affiliateItems, error } = await supabase
          .from('weekly_settlement_affiliate_items')
          .select(`
            *,
            brand:brand_id (
              store_name
            )
          `)
          .eq('settlement_id', settlement.id)
          .order('earned_at', { ascending: false });

        if (error) {
          console.error('Error loading affiliate items:', error);
          return;
        }

        // 어필리에이트 아이템을 SettlementDetail 형식에 맞게 변환
        const formattedItems = (affiliateItems || []).map(item => ({
          product_name: item.product_name,
          order_count: 1,
          total_amount: item.order_amount,
          commission: item.commission_amount,
          net_amount: item.commission_amount, // 어필리에이트는 커미션이 수익
        }));

        setSettlementDetails(formattedItems);
      }
    } catch (error) {
      console.error('Error loading settlement details:', error);
    }

    setDetailDialogOpen(true);
  };

  const handleProcessSettlement = async (settlement: WeeklySettlement) => {
    if (!window.confirm(t('brandSettlement.confirmProcess', { name: settlement.store_name || settlement.seller?.store_name }))) {
      return;
    }

    try {
      const { error } = await supabase
        .from('weekly_settlements')
        .update({
          status: 'processing',
          processed_at: new Date().toISOString()
        })
        .eq('id', settlement.id);

      if (error) {
        console.error('Error processing settlement:', error);
        alert(t('brandSettlement.processError'));
        return;
      }

      alert(t('brandSettlement.processSuccess'));
      loadSettlements(); // 목록 새로고침
    } catch (error) {
      console.error('Error in handleProcessSettlement:', error);
      alert(t('brandSettlement.processException'));
    }
  };

  const handleExportExcel = () => {
    // 엑셀 다운로드용 데이터 준비
    const excelData = filteredSettlements.map(settlement => ({
      '구분': settlement.settlement_type === 'sales' ? t('brandSettlement.salesLabel') : t('brandSettlement.affiliateLabel'),
      '브랜드/크리에이터명': settlement.store_name || settlement.seller?.store_name || t('brandSettlement.unknownStore'),
      '셀러ID': settlement.seller_id,
      '연도': settlement.year,
      '주차': settlement.week_number,
      '정산기간': `${new Date(settlement.period_start_date).toLocaleDateString()} ~ ${new Date(settlement.period_end_date).toLocaleDateString()}`,
      '총 매출': settlement.gross_sales,
      '차감(환불)': settlement.refund_amount,
      '순 매출': settlement.net_sales,
      '플랫폼 수수료율': settlement.commission_rate + '%',
      '플랫폼 수수료': settlement.commission_amount,
      '결제 수수료율': (settlement.seller?.payment_fee_rate || 3.6) + '%',
      '결제 수수료': settlement.payment_fee_amount || 0,
      '정산금액': settlement.final_amount,
      '상태': getStatusText(settlement.status),
      '지급예정일': settlement.due_date.slice(0, 10),
      '생성일': new Date(settlement.created_at).toLocaleDateString(),
      '비고': 'Affiliate 주문은 플랫폼 수수료 동적 적용',
    }));

    // 워크시트 생성
    const ws = XLSX.utils.json_to_sheet(excelData);

    // 컬럼 너비 설정
    const colWidths = [
      { wch: 12 }, // 구분
      { wch: 25 }, // 브랜드명
      { wch: 30 }, // 셀러ID
      { wch: 8 },  // 연도
      { wch: 8 },  // 주차
      { wch: 25 }, // 정산기간
      { wch: 15 }, // 총 매출
      { wch: 15 }, // 차감
      { wch: 15 }, // 순 매출
      { wch: 12 }, // 플랫폼 수수료율
      { wch: 15 }, // 플랫폼 수수료
      { wch: 12 }, // 결제 수수료율
      { wch: 15 }, // 결제 수수료
      { wch: 15 }, // 정산금액
      { wch: 10 }, // 상태
      { wch: 12 }, // 예정일
      { wch: 12 }, // 생성일
      { wch: 30 }, // 비고
    ];
    ws['!cols'] = colWidths;

    // 워크북 생성
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, t('brandSettlement.excelSheetName'));

    // 파일명 생성 (현재 날짜 포함)
    const today = new Date();
    const fileName = `${t('brandSettlement.excelFileName')}_${today.getFullYear()}${String(today.getMonth() + 1).padStart(2, '0')}${String(today.getDate()).padStart(2, '0')}.xlsx`;

    // 파일 다운로드
    XLSX.writeFile(wb, fileName);
  };

  const handleOpenCommissionDialog = (settlement: WeeklySettlement) => {
    setSelectedSeller({
      id: settlement.seller_id,
      store_name: settlement.store_name || settlement.seller?.store_name || t('brandSettlement.unknownStore'),
      commission_rate: settlement.commission_rate
    });
    setNewCommissionRate(settlement.commission_rate.toString());
    setCommissionDialogOpen(true);
  };

  const handleUpdateCommissionRate = async () => {
    if (!selectedSeller) return;

    const rate = parseFloat(newCommissionRate);
    if (isNaN(rate) || rate < 0 || rate > 100) {
      alert(t('brandSettlement.commissionRangeError'));
      return;
    }

    try {
      const { error } = await supabase
        .from('seller_profile')
        .update({ commission_rate: rate })
        .eq('id', selectedSeller.id);

      if (error) {
        console.error('Error updating commission rate:', error);
        alert(t('brandSettlement.commissionUpdateError'));
        return;
      }

      alert(`${selectedSeller.store_name}의 수수료율이 ${rate}%로 변경되었습니다.\n다음 정산부터 새로운 수수료율이 적용됩니다.`);
      setCommissionDialogOpen(false);
      loadSettlements(); // 목록 새로고침
    } catch (error) {
      console.error('Error in handleUpdateCommissionRate:', error);
      alert(t('brandSettlement.commissionUpdateException'));
    }
  };

  const handleGenerateSettlement = async () => {
    try {
      setGenerating(true);

      // SQL 함수 호출
      const { data, error } = await supabase.rpc('generate_weekly_settlements', {
        p_week_start: weekStart || null,
        p_week_end: weekEnd || null,
      });

      if (error) {
        console.error('Error generating settlements:', error);
        alert(t('brandSettlement.generateError', { message: error.message }));
        return;
      }

      console.log('Settlement generation result:', data);

      if (data && data.success) {
        alert(`Settlement created! Sales: ${data.sales_settlements}, Affiliate: ${data.affiliate_settlements}, Total: ${data.total_settlements}`);
        setGenerateDialogOpen(false);
        setWeekStart('');
        setWeekEnd('');
        loadSettlements(); // 목록 새로고침
      } else {
        alert(t('brandSettlement.generateFailed'));
      }
    } catch (error) {
      console.error('Error in handleGenerateSettlement:', error);
      alert(t('brandSettlement.generateException'));
    } finally {
      setGenerating(false);
    }
  };

  const filteredSettlements = settlements.filter(settlement => {
    const brandName = settlement.store_name || settlement.seller?.store_name || '';
    const matchesSearch = brandName.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         settlement.seller_id.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || settlement.status === statusFilter;
    const matchesType = typeFilter === 'all' || settlement.settlement_type === typeFilter;
    const matchesYear = yearFilter === 'all' || settlement.year === parseInt(yearFilter);
    const matchesWeek = weekFilter === 'all' || settlement.week_number === parseInt(weekFilter);

    return matchesSearch && matchesStatus && matchesType && matchesYear && matchesWeek;
  });

  const paginatedSettlements = filteredSettlements.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // 판매 정산 관련
  const salesSettlements = settlements.filter(s => s.settlement_type === 'sales');
  const totalSalesAmount = salesSettlements.reduce((sum, s) => sum + s.final_amount, 0);
  const totalSalesCommission = salesSettlements.reduce((sum, s) => sum + s.commission_amount, 0);
  const totalSalesGross = salesSettlements.reduce((sum, s) => sum + s.gross_sales, 0);

  // 어필리에이트 정산 관련
  const affiliateSettlements = settlements.filter(s => s.settlement_type === 'affiliate');
  const totalAffiliateAmount = affiliateSettlements.reduce((sum, s) => sum + s.final_amount, 0);
  const totalAffiliateGross = affiliateSettlements.reduce((sum, s) => sum + s.gross_sales, 0);

  // 전체 통계
  const pendingCount = settlements.filter(s => s.status === 'pending').length;

  return (
    <Box sx={{ 
      p: 3, 
      backgroundColor: '#f8fafc',
      minHeight: '100vh'
    }}>
      <Typography 
        variant="h4" 
        sx={{ 
          fontWeight: 600, 
          mb: 3,
          color: '#1f2937'
        }}
      >
            {t('brandSettlement.title')}
      </Typography>

      {/* 정산 요약 카드 */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} md={2.4}>
          <Card sx={{
            backgroundColor: '#ffffff',
            borderRadius: 2,
            border: '1px solid #e5e7eb',
            boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
            transition: 'all 0.2s ease-in-out',
            '&:hover': {
              boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
              transform: 'translateY(-1px)'
            }
          }}>
            <CardContent sx={{ p: 3 }}>
              <Stack direction="row" alignItems="center" spacing={2}>
                <Box sx={{
                  p: 1.5,
                  borderRadius: 1.5,
                  backgroundColor: '#dbeafe'
                }}>
                  <AttachMoney sx={{ color: '#3b82f6' }} />
                </Box>
                <Box>
                  <Typography
                    variant="body2"
                    sx={{
                      color: '#6b7280',
                      fontSize: '14px',
                      mb: 0.5
                    }}
                  >
                    {t('brandSettlement.totalSalesBeforeFee')}
                  </Typography>
                  <Typography
                    variant="h5"
                    sx={{
                      color: '#3b82f6',
                      fontWeight: 700
                    }}
                  >
                    ₩{totalSalesGross.toLocaleString()}
                  </Typography>
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={2.4}>
          <Card sx={{
            backgroundColor: '#ffffff',
            borderRadius: 2,
            border: '1px solid #e5e7eb',
            boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
            transition: 'all 0.2s ease-in-out',
            '&:hover': {
              boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
              transform: 'translateY(-1px)'
            }
          }}>
            <CardContent sx={{ p: 3 }}>
              <Stack direction="row" alignItems="center" spacing={2}>
                <Box sx={{
                  p: 1.5,
                  borderRadius: 1.5,
                  backgroundColor: '#fce7f3'
                }}>
                  <Assessment sx={{ color: '#ec4899' }} />
                </Box>
                <Box>
                  <Typography
                    variant="body2"
                    sx={{
                      color: '#6b7280',
                      fontSize: '14px',
                      mb: 0.5
                    }}
                  >
                    {t('brandSettlement.brandSettlementAmount')}
                  </Typography>
                  <Typography
                    variant="h5"
                    sx={{
                      color: '#ec4899',
                      fontWeight: 700
                    }}
                  >
                    ₩{totalSalesAmount.toLocaleString()}
                  </Typography>
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={2.4}>
          <Card sx={{
            backgroundColor: '#ffffff',
            borderRadius: 2,
            border: '1px solid #e5e7eb',
            boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
            transition: 'all 0.2s ease-in-out',
            '&:hover': {
              boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
              transform: 'translateY(-1px)'
            }
          }}>
            <CardContent sx={{ p: 3 }}>
              <Stack direction="row" alignItems="center" spacing={2}>
                <Box sx={{
                  p: 1.5,
                  borderRadius: 1.5,
                  backgroundColor: '#d1fae5'
                }}>
                  <TrendingUp sx={{ color: '#10b981' }} />
                </Box>
                <Box>
                  <Typography
                    variant="body2"
                    sx={{
                      color: '#6b7280',
                      fontSize: '14px',
                      mb: 0.5
                    }}
                  >
                    {t('brandSettlement.creatorEarningsPayout')}
                  </Typography>
                  <Typography
                    variant="h5"
                    sx={{
                      color: '#10b981',
                      fontWeight: 700
                    }}
                  >
                    ₩{totalAffiliateAmount.toLocaleString()}
                  </Typography>
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={2.4}>
          <Card sx={{
            backgroundColor: '#ffffff',
            borderRadius: 2,
            border: '1px solid #e5e7eb',
            boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
            transition: 'all 0.2s ease-in-out',
            '&:hover': {
              boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
              transform: 'translateY(-1px)'
            }
          }}>
            <CardContent sx={{ p: 3 }}>
              <Stack direction="row" alignItems="center" spacing={2}>
                <Box sx={{
                  p: 1.5,
                  borderRadius: 1.5,
                  backgroundColor: '#e0e7ff'
                }}>
                  <AccountBalance sx={{ color: '#6366f1' }} />
                </Box>
                <Box>
                  <Typography
                    variant="body2"
                    sx={{
                      color: '#6b7280',
                      fontSize: '14px',
                      mb: 0.5
                    }}
                  >
                    {t('brandSettlement.platformFeeRevenue')}
                  </Typography>
                  <Typography
                    variant="h5"
                    sx={{
                      color: '#6366f1',
                      fontWeight: 700
                    }}
                  >
                    ₩{totalSalesCommission.toLocaleString()}
                  </Typography>
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={2.4}>
          <Card sx={{
            backgroundColor: '#ffffff',
            borderRadius: 2,
            border: '1px solid #e5e7eb',
            boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
            transition: 'all 0.2s ease-in-out',
            '&:hover': {
              boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
              transform: 'translateY(-1px)'
            }
          }}>
            <CardContent sx={{ p: 3 }}>
              <Stack direction="row" alignItems="center" spacing={2}>
                <Box sx={{
                  p: 1.5,
                  borderRadius: 1.5,
                  backgroundColor: '#fef3c7'
                }}>
                  <Schedule sx={{ color: '#f59e0b' }} />
                </Box>
                <Box>
                  <Typography
                    variant="body2"
                    sx={{
                      color: '#6b7280',
                      fontSize: '14px',
                      mb: 0.5
                    }}
                  >
                    {t('brandSettlement.pendingSettlement')}
                  </Typography>
                  <Typography
                    variant="h5"
                    sx={{
                      color: '#f59e0b',
                      fontWeight: 700
                    }}
                  >
                    {pendingCount}건
                  </Typography>
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* 매출 트렌드 차트 */}
      <Paper sx={{ 
        p: 3, 
        mb: 3,
        backgroundColor: '#ffffff',
        borderRadius: 2,
        border: '1px solid #e5e7eb',
        boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)'
      }}>
        <Typography 
          variant="h6" 
          sx={{ 
            fontWeight: 600,
            color: '#1f2937',
            mb: 3
          }}
        >
          {t('brandSettlement.monthlySettlementTrend')}
        </Typography>
        <ResponsiveContainer width="100%" height={300}>
          <LineChart data={salesTrendData}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
            <XAxis dataKey="month" />
            <YAxis />
            <RechartsTooltip formatter={(value) => [`₩${Number(value).toLocaleString()}`, t('brandSettlement.settlementAmount')]} />
            <Line type="monotone" dataKey="sales" stroke="#3b82f6" strokeWidth={2} />
          </LineChart>
        </ResponsiveContainer>
      </Paper>

      {/* 필터 및 검색 */}
      <Paper sx={{ 
        p: 3,
        backgroundColor: '#ffffff',
        borderRadius: 2,
        border: '1px solid #e5e7eb',
        boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)'
      }}>
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              placeholder={t('brandSettlement.searchPlaceholder')}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12} md={1.5}>
            <FormControl fullWidth>
              <InputLabel>{t('brandSettlement.year')}</InputLabel>
              <Select
                value={yearFilter}
                label={t('brandSettlement.year')}
                onChange={(e) => {
                  setYearFilter(e.target.value);
                  setWeekFilter('all'); // 연도 변경시 주차 필터 초기화
                }}
              >
                <MenuItem value="all">{t('brandSettlement.allYears')}</MenuItem>
                {availableYears.map(year => (
                  <MenuItem key={year} value={year.toString()}>{year}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={1.5}>
            <FormControl fullWidth disabled={yearFilter === 'all'}>
              <InputLabel>{t('brandSettlement.weekNumber')}</InputLabel>
              <Select
                value={weekFilter}
                label={t('brandSettlement.weekNumber')}
                onChange={(e) => setWeekFilter(e.target.value)}
              >
                <MenuItem value="all">{t('brandSettlement.allYears')}</MenuItem>
                {availableWeeks.map(week => (
                  <MenuItem key={week} value={week.toString()}>{week}{t('brandSettlement.week')}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={2}>
            <FormControl fullWidth>
              <InputLabel>{t('brandSettlement.settlementType')}</InputLabel>
              <Select
                value={typeFilter}
                label={t('brandSettlement.settlementType')}
                onChange={(e) => setTypeFilter(e.target.value as any)}
              >
                <MenuItem value="all">{t('brandSettlement.allYears')}</MenuItem>
                <MenuItem value="sales">{t('brandSettlement.salesType')}</MenuItem>
                <MenuItem value="affiliate">{t('brandSettlement.affiliateType')}</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={2}>
            <FormControl fullWidth>
              <InputLabel>{t('brandSettlement.settlementStatus')}</InputLabel>
              <Select
                value={statusFilter}
                label={t('brandSettlement.settlementStatus')}
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <MenuItem value="all">{t('brandSettlement.allYears')}</MenuItem>
                <MenuItem value="pending">{t('brandSettlement.pendingStatus')}</MenuItem>
                <MenuItem value="processing">{t('brandSettlement.processingStatus')}</MenuItem>
                <MenuItem value="completed">{t('brandSettlement.completedStatus')}</MenuItem>
                <MenuItem value="cancelled">{t("common.cancel")}</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={1}>
            <Button
              fullWidth
              variant="outlined"
              startIcon={<Download />}
              onClick={handleExportExcel}
              sx={{
                height: '56px',
                borderColor: '#d1d5db',
                color: '#374151',
                backgroundColor: '#ffffff',
                '&:hover': {
                  borderColor: '#9ca3af',
                  backgroundColor: '#f9fafb'
                }
              }}
            >
              {t('brandSettlement.excelButton')}
            </Button>
          </Grid>
          <Grid item xs={12} md={1}>
            <Button
              fullWidth
              variant="contained"
              startIcon={<Add />}
              onClick={() => setGenerateDialogOpen(true)}
              sx={{
                height: '56px',
                backgroundColor: '#3b82f6',
                '&:hover': {
                  backgroundColor: '#2563eb'
                }
              }}
            >
              {t('brandSettlement.generateButton')}
            </Button>
          </Grid>
        </Grid>

        {/* 정산 테이블 */}
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>{t('brandSettlement.division')}</TableCell>
                <TableCell>{t('brandSettlement.brandCreatorName')}</TableCell>
                <TableCell>{t('brandSettlement.week')}</TableCell>
                <TableCell>{t('brandSettlement.settlementPeriod')}</TableCell>
                <TableCell align="right">{t('brandSettlement.grossSales')}</TableCell>
                <TableCell align="right">{t('brandSettlement.refundDeduction')}</TableCell>
                <TableCell align="right">{t('brandSettlement.netSales')}</TableCell>
                <TableCell align="right">{t('brandSettlement.commission')}</TableCell>
                <TableCell align="right">{t('brandSettlement.settlementAmount')}</TableCell>
                <TableCell>{t('common.status')}</TableCell>
                <TableCell>{t('brandSettlement.scheduledDate')}</TableCell>
                <TableCell align="center">{t('brandSettlement.actions')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={12} align="center">
                    <Typography>{t('brandSettlement.loadingData')}</Typography>
                  </TableCell>
                </TableRow>
              ) : paginatedSettlements.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={12} align="center">
                    <Typography>{t('brandSettlement.noSettlementHistory')}</Typography>
                  </TableCell>
                </TableRow>
              ) : (
                paginatedSettlements.map((settlement) => (
                  <TableRow key={settlement.id} hover>
                    <TableCell>
                      <Chip
                        label={settlement.settlement_type === 'sales' ? t('brandSettlement.salesChip') : t('brandSettlement.affiliateChip')}
                        color={settlement.settlement_type === 'sales' ? 'primary' : 'success'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Box>
                        <Typography variant="body2" fontWeight="bold">
                          {settlement.store_name || settlement.seller?.store_name || t('brandSettlement.unknownStore')}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {settlement.seller_id.slice(0, 8)}...
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>{settlement.week_number}{t('brandSettlement.week')}</TableCell>
                    <TableCell>
                      {new Date(settlement.period_start_date).toLocaleDateString('ko-KR', { month: 'short', day: 'numeric' })} ~ {new Date(settlement.period_end_date).toLocaleDateString('ko-KR', { month: 'short', day: 'numeric' })}
                    </TableCell>
                    <TableCell align="right">
                      ₩{settlement.gross_sales.toLocaleString()}
                    </TableCell>
                    <TableCell align="right">
                      <Typography color="error">
                        ₩{settlement.refund_amount.toLocaleString()}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      ₩{settlement.net_sales.toLocaleString()}
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2" color="error">
                        -₩{settlement.commission_amount.toLocaleString()}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        ({settlement.commission_rate}%)
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2" fontWeight="bold" color="primary">
                        ₩{settlement.final_amount.toLocaleString()}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={getStatusText(settlement.status)}
                        color={getStatusColor(settlement.status) as any}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>{settlement.due_date.slice(0, 10)}</TableCell>
                    <TableCell align="center">
                      <Stack direction="row" spacing={1} justifyContent="center">
                        <Tooltip title={t('brandSettlement.viewDetail')}>
                          <IconButton
                            size="small"
                            onClick={() => handleViewDetail(settlement)}
                          >
                            <Visibility />
                          </IconButton>
                        </Tooltip>
                        {settlement.status === 'pending' && (
                          <Tooltip title={t('brandSettlement.processSettlement')}>
                            <IconButton
                              size="small"
                              color="primary"
                              onClick={() => handleProcessSettlement(settlement)}
                            >
                              <CheckCircle />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={filteredSettlements.length}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[5, 10, 25, 50]}
          labelRowsPerPage={t('brandSettlement.rowsPerPage')}
          labelDisplayedRows={({ from, to, count }) => `${from}-${to} / ${count}`}
        />
      </Paper>

      {/* 정산 상세 다이얼로그 */}
      <Dialog
        open={detailDialogOpen}
        onClose={() => setDetailDialogOpen(false)}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          {t('brandSettlement.detailTitle', { name: selectedSettlement?.store_name || selectedSettlement?.seller?.store_name })}
        </DialogTitle>
        <DialogContent>
          {selectedSettlement && (
            <Box>
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={6}>
                  <Typography variant="subtitle2">{t('brandSettlement.settlementId')}</Typography>
                  <Typography variant="body1">{selectedSettlement.id}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="subtitle2">{t('brandSettlement.settlementTypeLabel')}</Typography>
                  <Typography variant="body1">
                    {selectedSettlement.settlement_type === 'sales' ? t('brandSettlement.salesLabel') : t('brandSettlement.affiliateLabel')}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="subtitle2">{t('brandSettlement.settlementPeriodLabel')}</Typography>
                  <Typography variant="body1">
                    {t('brandSettlement.weekNumberLabel', { year: selectedSettlement.year, week: selectedSettlement.week_number })}
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="subtitle2">{t('brandSettlement.periodLabel')}</Typography>
                  <Typography variant="body1">
                    {new Date(selectedSettlement.period_start_date).toLocaleDateString('ko-KR')} ~ {new Date(selectedSettlement.period_end_date).toLocaleDateString('ko-KR')}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="subtitle2">{t('brandSettlement.grossSalesLabel')}</Typography>
                  <Typography variant="body1">₩{selectedSettlement.gross_sales.toLocaleString()}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="subtitle2">{t('brandSettlement.refundDeductionLabel')}</Typography>
                  <Typography variant="body1" color="error">
                    -₩{selectedSettlement.refund_amount.toLocaleString()}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="subtitle2">{t('brandSettlement.netSalesLabel')}</Typography>
                  <Typography variant="body1">₩{selectedSettlement.net_sales.toLocaleString()}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="subtitle2">{t('brandSettlement.platformFeeLabel', { rate: selectedSettlement.commission_rate })}</Typography>
                  <Typography variant="body1" color="error">
                    -₩{selectedSettlement.commission_amount.toLocaleString()}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="subtitle2">{t('brandSettlement.paymentFeeLabel', { rate: selectedSettlement.seller?.payment_fee_rate || 3.6 })}</Typography>
                  <Typography variant="body1" color="error">
                    -₩{(selectedSettlement.payment_fee_amount || 0).toLocaleString()}
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <Divider sx={{ my: 1 }} />
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="subtitle2">{t('brandSettlement.finalSettlementAmount')}</Typography>
                  <Typography variant="h6" color="primary">
                    ₩{selectedSettlement.final_amount.toLocaleString()}
                  </Typography>
                </Grid>
              </Grid>
              
              <Divider sx={{ my: 2 }} />
              
              <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
                {t('brandSettlement.sellerInfo')}
              </Typography>
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={6}>
                  <Typography variant="subtitle2">{t('brandSettlement.businessNumber')}</Typography>
                  <Typography variant="body1">{selectedSettlement.seller?.business_number || '-'}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="subtitle2">{t('brandSettlement.businessType')}</Typography>
                  <Typography variant="body1">{selectedSettlement.seller?.business_type || '-'}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="subtitle2">{t('brandSettlement.ownerName')}</Typography>
                  <Typography variant="body1">{selectedSettlement.seller?.owner_name || '-'}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="subtitle2">{t('brandSettlement.addressLabel')}</Typography>
                  <Typography variant="body1">{selectedSettlement.seller?.address || '-'}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="subtitle2">{t('brandSettlement.contactEmail')}</Typography>
                  <Typography variant="body1">{selectedSettlement.seller?.contact_email || '-'}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="subtitle2">{t('brandSettlement.contactPhone')}</Typography>
                  <Typography variant="body1">{selectedSettlement.seller?.contact_phone || '-'}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="subtitle2">{t('brandSettlement.bankName')}</Typography>
                  <Typography variant="body1">{selectedSettlement.seller?.bank_name || '-'}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="subtitle2">{t('brandSettlement.bankAccount')}</Typography>
                  <Typography variant="body1">{selectedSettlement.seller?.bank_account || '-'}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="subtitle2">{t('brandSettlement.bankAccountHolder')}</Typography>
                  <Typography variant="body1">{selectedSettlement.seller?.bank_account_holder || '-'}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="subtitle2">{t('brandSettlement.statusLabel')}</Typography>
                  <Typography variant="body1">{selectedSettlement.seller?.status || '-'}</Typography>
                </Grid>
              </Grid>
              
              <Divider sx={{ my: 2 }} />
              
              <Typography variant="h6" gutterBottom>
                {t('brandSettlement.productSettlementHistory')}
              </Typography>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>{t('brandSettlement.productNameCol')}</TableCell>
                      <TableCell align="center">Affiliate</TableCell>
                      <TableCell align="right">{t('brandSettlement.orderCount')}</TableCell>
                      <TableCell align="right">{t('brandSettlement.totalAmount')}</TableCell>
                      <TableCell align="right">{t('brandSettlement.platformFeeCol')}</TableCell>
                      <TableCell align="right">{t('brandSettlement.paymentFeeCol')}</TableCell>
                      <TableCell align="right">{t('brandSettlement.settlementAmount')}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {settlementDetails.length > 0 ? (
                      settlementDetails.map((detail, index) => {
                        const isAffiliate = detail.is_affiliate || false;
                        const baseCommissionRate = selectedSettlement?.commission_rate || 10;
                        // 동적 수수료율 계산: 어필리에이트면 실제 크리에이터 수수료율만큼 차감
                        const affiliateCommissionRate = detail.affiliate_commission_rate || 0;
                        const actualCommissionRate = isAffiliate
                          ? Math.max(baseCommissionRate - affiliateCommissionRate, 0)
                          : baseCommissionRate;

                        // 결제 수수료 계산 (총액의 3.6%)
                        const paymentFee = detail.total_amount * 0.036;

                        return (
                          <TableRow key={index}>
                            <TableCell>{detail.product_name}</TableCell>
                            <TableCell align="center">
                              {isAffiliate ? (
                                <Chip label="Y" color="success" size="small" />
                              ) : (
                                <Chip label="N" color="default" size="small" variant="outlined" />
                              )}
                            </TableCell>
                            <TableCell align="right">{detail.order_count}</TableCell>
                            <TableCell align="right">₩{detail.total_amount.toLocaleString()}</TableCell>
                            <TableCell align="right">
                              <Stack direction="column" alignItems="flex-end">
                                {isAffiliate ? (
                                  <Stack direction="row" alignItems="center" spacing={0.5}>
                                    <Typography variant="caption" sx={{ textDecoration: 'line-through', color: 'text.secondary' }}>
                                      {baseCommissionRate}%
                                    </Typography>
                                    <Typography variant="body2" color="success.main" fontWeight="bold">
                                      {actualCommissionRate}%
                                    </Typography>
                                  </Stack>
                                ) : (
                                  <Typography variant="body2">{actualCommissionRate}%</Typography>
                                )}
                                <Typography variant="body2" color="error">
                                  -₩{detail.commission.toLocaleString()}
                                </Typography>
                              </Stack>
                            </TableCell>
                            <TableCell align="right">
                              <Stack direction="column" alignItems="flex-end">
                                <Typography variant="body2">3.6%</Typography>
                                <Typography variant="body2" color="error">
                                  -₩{Math.round(paymentFee).toLocaleString()}
                                </Typography>
                              </Stack>
                            </TableCell>
                            <TableCell align="right">
                              <Typography variant="body2" fontWeight="bold">
                                ₩{detail.net_amount.toLocaleString()}
                              </Typography>
                            </TableCell>
                          </TableRow>
                        );
                      })
                    ) : (
                      <TableRow>
                        <TableCell colSpan={7} align="center">
                          <Typography color="text.secondary">{t('brandSettlement.noDetailHistory')}</Typography>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailDialogOpen(false)}>{t("common.close")}</Button>
          <Button variant="contained" startIcon={<Download />}>
            {t('brandSettlement.downloadStatement')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* 정산 생성 다이얼로그 */}
      <Dialog
        open={generateDialogOpen}
        onClose={() => setGenerateDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {t('brandSettlement.generateDialogTitle')}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              {t('brandSettlement.generateDialogDesc')}
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label={t('brandSettlement.startDate')}
                  type="date"
                  value={weekStart}
                  onChange={(e) => setWeekStart(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  helperText={t('brandSettlement.startDateHelper')}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label={t('brandSettlement.endDate')}
                  type="date"
                  value={weekEnd}
                  onChange={(e) => setWeekEnd(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  helperText={t('brandSettlement.endDateHelper')}
                />
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setGenerateDialogOpen(false)} disabled={generating}>
            {t('common.cancel')}
          </Button>
          <Button
            variant="contained"
            onClick={handleGenerateSettlement}
            disabled={generating}
            startIcon={generating ? undefined : <CalendarToday />}
          >
            {generating ? t('brandSettlement.generating') : t('brandSettlement.generate')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* 수수료율 변경 다이얼로그 */}
      <Dialog
        open={commissionDialogOpen}
        onClose={() => setCommissionDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Stack direction="row" alignItems="center" spacing={1}>
            <Percent />
            <Typography variant="h6">{t('brandSettlement.commissionDialogTitle')}</Typography>
          </Stack>
        </DialogTitle>
        <DialogContent>
          {selectedSeller && (
            <Box sx={{ pt: 2 }}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {t('brandSettlement.commissionDialogDesc', { name: selectedSeller.store_name })}
              </Typography>
              <Typography variant="caption" color="warning.main" sx={{ mb: 3, display: 'block' }}>
                {t('brandSettlement.commissionWarning')}
              </Typography>
              <TextField
                fullWidth
                label={t('brandSettlement.commissionRateLabel')}
                type="number"
                value={newCommissionRate}
                onChange={(e) => setNewCommissionRate(e.target.value)}
                InputProps={{
                  endAdornment: <InputAdornment position="end">%</InputAdornment>,
                }}
                inputProps={{
                  min: 0,
                  max: 100,
                  step: 0.01
                }}
                helperText={t('brandSettlement.commissionHelper')}
                sx={{ mt: 2 }}
              />
              <Box sx={{ mt: 3, p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
                <Typography variant="caption" color="text.secondary">
                  {t('brandSettlement.currentCommissionRate', { rate: selectedSeller.commission_rate })}
                </Typography>
                <br />
                <Typography variant="caption" color="text.secondary">
                  {t('brandSettlement.newCommissionRate', { rate: newCommissionRate || '0' })}
                </Typography>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCommissionDialogOpen(false)}>
            {t('common.cancel')}
          </Button>
          <Button
            variant="contained"
            onClick={handleUpdateCommissionRate}
            color="primary"
          >
            {t('common.edit')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}